type OneMapResponse = {
  found?: number;
  results?: {
    LATITUDE: string;
    LONGITUDE: string;
    ADDRESS?: string;
    SEARCHVAL?: string;
    POSTAL?: string;
  }[];
};

export type SingaporePostalLookup = {
  lat: number;
  lng: number;
  address: string;
  postal: string;
};

type OsrmResponse = {
  code?: string;
  routes?: {
    legs: { duration: number }[];
    geometry: { coordinates: [number, number][] };
  }[];
};

const GEOCODE_DELAY_MS = 150;
const GEOCODE_RETRY_MS = 400;

/** Warehouse / hub postcodes used when OneMap lookup fails. */
const KNOWN_POSTAL_COORDS: Record<string, { lat: number; lng: number }> = {
  '533756': { lat: 1.343047, lng: 103.896903 }, // JTC Defu Industrial City
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function onemapUrl(postal: string): string {
  const params = new URLSearchParams({
    searchVal: postal,
    returnGeom: 'Y',
    getAddrDetails: 'Y',
    pageNum: '1',
  });
  return `/dispatch-api/onemap?${params.toString()}`;
}

function osrmUrl(coords: string): string {
  return `/dispatch-api/osrm?coords=${encodeURIComponent(coords)}`;
}

function knownPostalLookup(postal: string): SingaporePostalLookup | null {
  const known = KNOWN_POSTAL_COORDS[postal];
  if (!known) return null;
  return {
    lat: known.lat,
    lng: known.lng,
    postal,
    address: `Singapore ${postal}`,
  };
}

export async function fetchOneMapPostalLookup(
  postal: string,
): Promise<SingaporePostalLookup | null> {
  const normalized = normalizePostal(postal);
  if (normalized.length !== 6) return null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0) await sleep(GEOCODE_RETRY_MS);
    try {
      const res = await fetch(onemapUrl(normalized));
      const json = (await res.json()) as OneMapResponse & { error?: string };
      if (json.found && json.results?.length) {
        const hit = json.results[0]!;
        const lat = Number(hit.LATITUDE);
        const lng = Number(hit.LONGITUDE);
        if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
        const address =
          String(hit.ADDRESS ?? '').trim() ||
          String(hit.SEARCHVAL ?? '').trim() ||
          `Singapore ${normalized}`;
        return {
          lat,
          lng,
          postal: String(hit.POSTAL ?? normalized).trim() || normalized,
          address,
        };
      }
    } catch {
      /* try again or fall through */
    }
  }

  return knownPostalLookup(normalized);
}

export async function fetchOneMapPostal(postal: string): Promise<{ lat: number; lng: number } | null> {
  const hit = await fetchOneMapPostalLookup(postal);
  return hit ? { lat: hit.lat, lng: hit.lng } : null;
}

/**
 * Free-text OneMap search (mall / area / street). Used when a task has outlet/area
 * but no 6-digit postal for reverse matching against fleet stops.
 */
export async function fetchOneMapSearchCoords(
  query: string,
): Promise<{ lat: number; lng: number } | null> {
  const q = query.trim();
  if (q.length < 3) return null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0) await sleep(GEOCODE_RETRY_MS);
    try {
      const res = await fetch(onemapUrl(q));
      const json = (await res.json()) as OneMapResponse & { error?: string };
      if (json.found && json.results?.length) {
        const hit = json.results[0]!;
        const lat = Number(hit.LATITUDE);
        const lng = Number(hit.LONGITUDE);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
      }
    } catch {
      /* retry */
    }
  }
  return null;
}

export async function resolveSingaporePostal(
  postal: string,
  cacheLookup?: (postal: string) => Promise<{ lat: number; lng: number } | null>,
): Promise<{ lat: number; lng: number } | null> {
  const lookup = await resolveSingaporePostalLookup(postal, cacheLookup);
  return lookup ? { lat: lookup.lat, lng: lookup.lng } : null;
}

export async function resolveSingaporePostalLookup(
  postal: string,
  cacheLookup?: (postal: string) => Promise<{ lat: number; lng: number } | null>,
): Promise<SingaporePostalLookup | null> {
  const normalized = normalizePostal(postal);
  if (normalized.length !== 6) return null;

  const onemap = await fetchOneMapPostalLookup(normalized);
  if (onemap) return onemap;

  if (cacheLookup) {
    try {
      const cached = await cacheLookup(normalized);
      if (cached) {
        return {
          lat: cached.lat,
          lng: cached.lng,
          postal: normalized,
          address: `Singapore ${normalized}`,
        };
      }
    } catch {
      /* ignore cache errors */
    }
  }

  return knownPostalLookup(normalized);
}

export async function fetchOsrmRoute(
  waypoints: [number, number][],
): Promise<{ geometry: [number, number][]; legMinutes: number[] } | null> {
  if (waypoints.length < 2) return null;

  const coords = waypoints.map(([lat, lng]) => `${lng},${lat}`).join(';');
  const res = await fetch(osrmUrl(coords));
  if (!res.ok) return null;
  const json = (await res.json()) as OsrmResponse;
  if (json.code !== 'Ok' || !json.routes?.[0]) return null;

  const route = json.routes[0];
  const geometry = route.geometry.coordinates.map(([lng, lat]) => [lat, lng] as [number, number]);
  const legMinutes = route.legs.map((leg) => Math.max(3, Math.round(leg.duration / 60)));
  return { geometry, legMinutes };
}

export async function fetchOsrmSegment(
  from: [number, number],
  to: [number, number],
): Promise<{ geometry: [number, number][]; minutes: number } | null> {
  const result = await fetchOsrmRoute([from, to]);
  if (!result) return null;
  return { geometry: result.geometry, minutes: result.legMinutes[0] ?? 15 };
}

type OneMapRevGeocodeResponse = {
  GeocodeInfo?: {
    BUILDINGNAME?: string;
    BLOCK?: string;
    ROAD?: string;
    POSTALCODE?: string;
  }[];
};

function formatOneMapRevGeocodeRow(row: NonNullable<OneMapRevGeocodeResponse['GeocodeInfo']>[number]): string {
  const street = [row.BLOCK?.trim(), row.ROAD?.trim()].filter(Boolean).join(' ');
  const parts = [street, row.BUILDINGNAME?.trim(), row.POSTALCODE ? `Singapore ${row.POSTALCODE.trim()}` : '']
    .filter(Boolean);
  return parts.join(', ');
}

async function fetchOneMapReverseGeocodeOnce(
  lat: number,
  lng: number,
  buffer: number,
): Promise<string | null> {
  const params = new URLSearchParams({
    location: `${lat},${lng}`,
    buffer: String(buffer),
    addressType: 'All',
    otherFeatures: 'N',
  });
  const res = await fetch(`/dispatch-api/onemap-revgeocode?${params.toString()}`);
  if (!res.ok) return null;
  const json = (await res.json()) as OneMapRevGeocodeResponse;
  const row = json.GeocodeInfo?.[0];
  if (!row) return null;
  const formatted = formatOneMapRevGeocodeRow(row);
  return formatted || null;
}

export async function fetchOneMapReverseGeocode(lat: number, lng: number): Promise<string | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0) await sleep(GEOCODE_RETRY_MS);
    try {
      const buffer = attempt === 0 ? 40 : 120;
      const formatted = await fetchOneMapReverseGeocodeOnce(lat, lng, buffer);
      if (formatted) return formatted;
    } catch {
      /* retry */
    }
  }
  return null;
}

export async function enrichStopsWithAddresses<T extends { lat: number; lng: number }>(
  stops: T[],
): Promise<Array<T & { address?: string }>> {
  const enriched: Array<T & { address?: string }> = [];
  for (const stop of stops) {
    const address = await fetchOneMapReverseGeocode(stop.lat, stop.lng);
    enriched.push(address ? { ...stop, address } : stop);
    await sleep(GEOCODE_DELAY_MS);
  }
  return enriched;
}

export async function geocodePostalsSequential(
  postals: string[],
  lookup: (postal: string) => Promise<{ lat: number; lng: number } | null>,
): Promise<Map<string, { lat: number; lng: number } | null>> {
  const results = new Map<string, { lat: number; lng: number } | null>();
  for (const postal of postals) {
    if (results.has(postal)) continue;
    try {
      results.set(postal, await lookup(postal));
    } catch {
      results.set(postal, null);
    }
    await sleep(GEOCODE_DELAY_MS);
  }
  return results;
}

export function hubCoordsForPostal(hubPostal: string): [number, number] {
  const known = KNOWN_POSTAL_COORDS[normalizePostal(hubPostal)];
  if (known) return [known.lat, known.lng];
  return [1.3521, 103.8198];
}

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function nearestNeighborOrder<T extends { lat: number; lng: number }>(
  start: [number, number],
  stops: T[],
): T[] {
  const unvisited = [...stops];
  const optimized: T[] = [];
  let [currLat, currLng] = start;

  while (unvisited.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < unvisited.length; i += 1) {
      const dist = haversineKm(currLat, currLng, unvisited[i].lat, unvisited[i].lng);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    const next = unvisited.splice(bestIdx, 1)[0];
    optimized.push(next);
    currLat = next.lat;
    currLng = next.lng;
  }

  return optimized;
}

export function fakeCoordsForPostal(
  postal: string,
  hub: [number, number],
): [number, number] {
  const offset = postal ? [...postal].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) : Math.floor(Math.random() * 100);
  const lat = hub[0] + ((offset % 100) - 50) / 4000;
  const lng = hub[1] + (((Math.floor(offset / 100) % 100) - 50) / 4000);
  return [lat, lng];
}

export function normalizePostal(raw: string): string {
  return raw.replace(/\D/g, '');
}
